import React, { useEffect, useState } from 'react';
/**
 * `PersonalDetailDealerPage` captures personal details and address for the
 * dealer journey.
 *
 * Behaviour:
 * - Coordinates validation/update cycles for `PersonalDetailPage` and
 *   `AddressManager`.
 * - Updates journey personal details and (when empty) copies the address into
 *   single payment billing address.
 * - Navigates back to create plan or forward to summary.
 */
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import PersonalDetailPage from '../common/PersonalDetail/PersonalDetailPage';
import AddressManager from '../common/Address/AddressManager';
import { PageAbsolutePath, PageRelativePath } from '../../consts/Common';
import { AddressDetails, PersonalDetailType } from '../common/CommonTypes';
import PageHeader from '../common/PageHeader';
import { ButtonContinue } from '../common/Button/ButtonContinue';
import { ButtonBack } from '../common/Button/ButtonBack';
import { useJourney } from './Layout';
import { CommonValidation } from '../common/CommonValidation/commonValidation';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../app-state/Hooks';

const PersonalDetailDealerPage = (): JSX.Element => {
  const { journey, setPersonalDetails, setAddressSinglePayment, onPageChange } =
    useJourney();

  const { personalDetail, singlePaymentDetails } = journey;

  const [validateAddress, setValidateAddress] = useState<boolean | null>(null);
  const [updateAddress, setUpdateAddress] = useState<boolean | null>(null);
  const [validatePersonalDetail, setValidatePersonalDetail] = useState<
    boolean | null
  >(null);
  const [updatePersonalDetail, setUpdatePersonalDetail] = useState<
    boolean | null
  >(null);
  const [goBack, setGoBack] = useState(false);
  const [goForward, setGoForward] = useState(false);
  const navigate = useNavigate();

  const cms = useAppSelector((s) => s.content.customerPage);
  const userToken = useAppSelector((s) => s.user.token);

  const { t } = useTranslation();

  useEffect(() => {
    if (updateAddress === false && goForward === true) {
      updateBillingAddress(personalDetail.addressDetails);
      onPageChange(PageAbsolutePath.PersonalDetails, isPageValid());
      navigate(PageRelativePath.Summary);
    }

    if (updateAddress === false && goBack === true) {
      onPageChange(PageAbsolutePath.PersonalDetails, isPageValid());
      navigate(PageRelativePath.CreatePlan);
    }
  }, [updateAddress]);

  useEffect(() => {
    if (updatePersonalDetail === false) {
      setUpdateAddress(true);
    }
  }, [updatePersonalDetail]);

  useEffect(() => {
    if (isPageValid()) {
      setUpdatePersonalDetail(true);
    }
  }, [validatePersonalDetail, validateAddress]);

  const onPersonalDetailValidated = (isPDValid: boolean) => {
    if (isPDValid === true) {
      setValidatePersonalDetail(false);
    } else {
      setValidatePersonalDetail(null);
      setGoForward(false);
    }
  };

  const onAddressValidated = (isAddressValid: boolean) => {
    if (isAddressValid === true) {
      setValidateAddress(false);
    } else {
      setValidateAddress(null);
      setGoForward(false);
    }
  };

  const onAddressUpdate = (address: AddressDetails) => {
    const newPersonalDetail = {
      ...personalDetail,
      addressDetails: address
    };

    setPersonalDetails(newPersonalDetail);
    setUpdateAddress(false);
  };

  const onUpdatePersonalDetail = (pd: PersonalDetailType) => {
    setPersonalDetails(pd);
    setUpdatePersonalDetail(false);
  };

  const onBack = () => {
    setUpdatePersonalDetail(true);
    setGoBack(true);
  };

  const onCheckout = () => {
    setValidateAddress(true);
    setValidatePersonalDetail(true);
    setGoForward(true);
  };

  const isPageValid = (): boolean => {
    const isValid =
      validatePersonalDetail === false && validateAddress === false;

    return isValid;
  };

  const updateBillingAddress = (address: AddressDetails) => {
    if (addressIsEmpty(singlePaymentDetails.billingAddress)) {
      setAddressSinglePayment(address);
    }
  };

  const addressIsEmpty = (address: AddressDetails): boolean => {
    const isAddressEmpty =
      CommonValidation.isEmpty(address.addressLine1) &&
      CommonValidation.isEmpty(address.addressLine2) &&
      CommonValidation.isEmpty(address.townOrCity) &&
      CommonValidation.isEmpty(address.country) &&
      CommonValidation.isEmpty(address.region) &&
      CommonValidation.isEmpty(address.postCode);
    return isAddressEmpty;
  };

  return (
    <>
      <Helmet>
        <title>{t('page-title.personal-details')}</title>
      </Helmet>

      <div>
        <PageHeader
          title={cms.title}
          description={cms.description}
          information={cms.information}
          warning={cms.warning}
          descriptionClass='p2'
        />
        <PersonalDetailPage
          initialPersonalDetail={personalDetail}
          validate={validatePersonalDetail || false}
          update={updatePersonalDetail || false}
          token={userToken}
          onValidated={onPersonalDetailValidated}
          onUpdate={onUpdatePersonalDetail}
          showEmailConfirmation={true}
          dobTooltipHeader={undefined}
          emailTooltipHeader={t('personal-details.dealer-email-tooltip-header')}
          phoneNumberTooltipHeader={t(
            'personal-details.dealer-phone-number-tooltip-header'
          )}
          isDoBOptional={cms.isDateOfBirthOptional}
        />

        <div className='control'>
          <AddressManager
            findAddressTitle={t('personal-details.find-address')}
            initialAddress={personalDetail?.addressDetails}
            update={updateAddress || false}
            validate={validateAddress || false}
            token={userToken}
            onAddressUpdate={onAddressUpdate}
            onAddressValidated={onAddressValidated}
            allowManualEntry
          />
        </div>
        <div className='flex-row form-nav mobile-width-90pc'>
          <ButtonBack
            name='btnBack'
            onClick={onBack}
            text={t('customer-button.back-buttont-text')}
          />
          <ButtonContinue
            name='btn-checkout'
            text={t('customer-button.button-checkout-text')}
            onClick={onCheckout}
          />
        </div>
      </div>
    </>
  );
};

export default PersonalDetailDealerPage;
